import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  LinearProgress,
  Card,
  CardContent,
  Chip,
  Alert,
  Fade,
  useTheme
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { Quiz, AssessmentQuestion, LearningStyleProfile } from '../../types/learning';

interface AdaptiveQuizProps {
  quiz: Quiz;
  learningStyle: LearningStyleProfile;
  onComplete: (score: number, responses: any[]) => void;
  onCancel: () => void;
}

interface QuizResponse {
  questionId: string;
  answer: any;
  isCorrect: boolean;
  timeSpent: number;
  confidence: number;
}

const AdaptiveQuiz: React.FC<AdaptiveQuizProps> = ({
  quiz,
  learningStyle,
  onComplete,
  onCancel
}) => {
  const theme = useTheme();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState<QuizResponse[]>([]);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [quizStartTime] = useState<number>(Date.now());
  const [questionStartTime, setQuestionStartTime] = useState<number>(Date.now());
  const [showResults, setShowResults] = useState(false);

  const currentQuestion = quiz.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;

  const handleComplete = useCallback(() => {
    const correctAnswers = responses.filter(r => r.isCorrect).length;
    const score = responses.length > 0 ? correctAnswers / responses.length : 0;
    
    setShowResults(true);
    onComplete(score, responses);
  }, [responses, onComplete]);

  useEffect(() => {
    setQuestionStartTime(Date.now());
    if (quiz.timeLimit) {
      setTimeRemaining(quiz.timeLimit * 60); // Convert minutes to seconds
    }
  }, [currentQuestionIndex, quiz.timeLimit]);

  useEffect(() => {
    if (timeRemaining === null) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 1) {
          handleComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, handleComplete]);

  const handleAnswerChange = (questionId: string, answer: any) => {
    const timeSpent = (Date.now() - questionStartTime) / 1000;
    const isCorrect = checkAnswer(currentQuestion, answer);
    
    setResponses(prev => {
      const existingIndex = prev.findIndex(r => r.questionId === questionId);
      const newResponse: QuizResponse = {
        questionId,
        answer,
        isCorrect,
        timeSpent,
        confidence: 4 // Default confidence, could be made interactive
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newResponse;
        return updated;
      } else {
        return [...prev, newResponse];
      }
    });
  };

  const checkAnswer = (question: AssessmentQuestion, answer: any): boolean => {
    if (!question.correctAnswer) return true; // For subjective questions
    
    if (question.questionType === 'multiple_choice') {
      return answer === question.correctAnswer;
    }
    
    if (question.questionType === 'likert_scale') {
      // For likert scale, consider answers within 1 point as correct
      const correctValue = question.correctAnswer;
      return Math.abs(answer - correctValue) <= 1;
    }
    
    return answer === question.correctAnswer;
  };

  const handleNext = () => {
    if (currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return theme.palette.success.main;
    if (score >= 0.6) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getScoreMessage = (score: number) => {
    if (score >= 0.9) return "Excellent! You've mastered this topic!";
    if (score >= 0.8) return "Great job! You have a solid understanding.";
    if (score >= 0.6) return "Good work! Consider reviewing some concepts.";
    if (score >= 0.4) return "Keep studying! You're making progress.";
    return "Don't give up! Review the material and try again.";
  };

  const renderQuestion = () => {
    if (!currentQuestion) return null;

    const currentResponse = responses.find(r => r.questionId === currentQuestion.id);

    switch (currentQuestion.questionType) {
      case 'multiple_choice':
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2, fontSize: '1.1rem' }}>
              {currentQuestion.questionText}
            </FormLabel>
            <RadioGroup
              value={currentResponse?.answer || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                  sx={{ mb: 1 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'likert_scale':
        return (
          <Box>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {currentQuestion.questionText}
            </Typography>
            <Box sx={{ px: 2 }}>
              <RadioGroup
                value={currentResponse?.answer || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, parseInt(e.target.value))}
                row
                sx={{ justifyContent: 'center' }}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <FormControlLabel
                    key={value}
                    value={value.toString()}
                    control={<Radio />}
                    label={value}
                    labelPlacement="top"
                  />
                ))}
              </RadioGroup>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption">Strongly Disagree</Typography>
                <Typography variant="caption">Strongly Agree</Typography>
              </Box>
            </Box>
          </Box>
        );

      case 'scenario':
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 2, fontSize: '1.1rem' }}>
              {currentQuestion.questionText}
            </FormLabel>
            <RadioGroup
              value={currentResponse?.answer || ''}
              onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                  sx={{ mb: 1 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      default:
        return (
          <Typography variant="body1">
            {currentQuestion.questionText}
          </Typography>
        );
    }
  };

  if (showResults) {
    const correctAnswers = responses.filter(r => r.isCorrect).length;
    const totalQuestions = quiz.questions.length;
    const score = correctAnswers / totalQuestions;
    const timeSpent = (Date.now() - quizStartTime) / 1000;

    return (
      <Fade in timeout={500}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ mb: 4 }}>
            <TrophyIcon sx={{ fontSize: 64, color: getScoreColor(score), mb: 2 }} />
            <Typography variant="h4" sx={{ mb: 2 }}>
              Quiz Complete!
            </Typography>
            <Typography variant="h2" sx={{ color: getScoreColor(score), mb: 2 }}>
              {Math.round(score * 100)}%
            </Typography>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {getScoreMessage(score)}
            </Typography>
          </Box>

          <Card sx={{ mb: 3, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Quiz Summary
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Correct Answers
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {correctAnswers}/{totalQuestions}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Time Spent
                  </Typography>
                  <Typography variant="h4">
                    {formatTime(timeSpent)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Average Time per Question
                  </Typography>
                  <Typography variant="h4">
                    {formatTime(timeSpent / totalQuestions)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => onComplete(score, responses)}
            >
              Continue Learning
            </Button>
            <Button 
              variant="outlined" 
              size="large"
              onClick={onCancel}
            >
              Back to Map
            </Button>
          </Box>
        </Paper>
      </Fade>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QuizIcon />
              {quiz.title}
            </Typography>
            {timeRemaining !== null && (
              <Chip 
                icon={<TimerIcon />}
                label={formatTime(timeRemaining)}
                color={timeRemaining < 60 ? 'error' : 'default'}
                variant="outlined"
              />
            )}
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="text.secondary">
            Question {currentQuestionIndex + 1} of {quiz.questions.length}
          </Typography>
        </Box>

        {/* Question */}
        <Box sx={{ mb: 4, minHeight: 200 }}>
          {renderQuestion()}
        </Box>

        {/* Learning Style Hint */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Learning Tip:</strong> Based on your learning style, 
            {learningStyle.visual > 0.7 && " try to visualize the concepts as you answer. "}
            {learningStyle.reading > 0.7 && " read each option carefully before choosing. "}
            {learningStyle.kinesthetic > 0.7 && " think about how you would apply this knowledge practically. "}
            {learningStyle.auditory > 0.7 && " consider discussing this topic with others after the quiz. "}
          </Typography>
        </Alert>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            variant="outlined"
          >
            Previous
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {quiz.questions.map((_, index) => {
              const response = responses.find(r => r.questionId === quiz.questions[index].id);
              return (
                <Box
                  key={index}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: response 
                      ? (response.isCorrect ? 'success.main' : 'error.main')
                      : index === currentQuestionIndex 
                        ? 'primary.main' 
                        : 'grey.300'
                  }}
                />
              );
            })}
          </Box>

          <Button
            onClick={handleNext}
            variant="contained"
            disabled={!responses.find(r => r.questionId === currentQuestion.id)}
          >
            {currentQuestionIndex === quiz.questions.length - 1 ? 'Finish Quiz' : 'Next'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdaptiveQuiz;
